// Pagina delle unità eroganti

import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    MedicalServices as MedicalServicesIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Sync as SyncIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Place as PlaceIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import ExecutionUnitForm from '../components/execution-units/ExecutionUnitForm';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { ExecutionUnit } from '../types';
import { 
    getExecutionUnits, 
    syncExecutionUnits,
    deleteExecutionUnit,
    DeleteOptions,
    DeleteResult 
} from '../services/executionUnitService';

const ExecutionUnitsPage: React.FC = () => {
    const { state, dispatch } = useAppContext();
    const [showForm, setShowForm] = useState(false);
    const [editingUnit, setEditingUnit] = useState<ExecutionUnit | null>(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedUnit, setSelectedUnit] = useState<ExecutionUnit | null>(null);
    
    // Check if user is admin (simplified check - adjust based on your auth system)
    const userIsAdmin = state.user?.role === 'admin';

    const handleEdit = (unit: ExecutionUnit) => {
        setEditingUnit(unit);
        setShowForm(true);
    };

    const handleDeleteClick = (unit: ExecutionUnit) => {
        setSelectedUnit(unit);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async (options: DeleteOptions) => {
        if (!selectedUnit) return;

        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            const result: DeleteResult = await deleteExecutionUnit(selectedUnit.id, options);
            
            // Rimuovi l'unità dalla lista locale
            dispatch({ 
                type: 'DELETE_EXECUTION_UNIT', 
                payload: selectedUnit.id 
            });
            
            // Messaggio di successo dettagliato
            if (options.isHardDelete && result.details) {
                const { local_deleted, recup_deleted, recup_id } = result.details;
                let successMessage = result.message;
                
                if (recup_id) {
                    successMessage += local_deleted && recup_deleted
                        ? ' (eliminata da database locale e RECUP)'
                        : local_deleted && !recup_deleted
                        ? ' (eliminata da database locale, errore RECUP)'
                        : ' (stato parziale)';
                }
                
                setSuccess(successMessage);
            } else {
                setSuccess(result.message || 'Unità erogante eliminata con successo');
            }
            
            setShowDeleteDialog(false);
            setSelectedUnit(null);
            
            // Ricarica i dati per essere sicuri
            await loadExecutionUnits();
        } catch (err: any) {
            console.error('Errore nell\'eliminazione dell\'unità erogante:', err);
            
            let errorMessage = 'Errore nell\'eliminazione dell\'unità erogante';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (unit: ExecutionUnit) => {
        setLoading(true);
        setError(null);
        try {
            const isUpdate = !!unit.id;
            const url = isUpdate 
                ? `/api/execution-units/${unit.id}`
                : '/api/execution-units';
            
            const response = await fetch(url, {
                method: isUpdate ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(unit)
            });
            
            if (!response.ok) {
                throw new Error(`Errore nel ${isUpdate ? 'aggiornamento' : 'salvataggio'} dell'unità erogante`);
            }
            
            const result = await response.json();
            
            if (isUpdate) {
                dispatch({ 
                    type: 'UPDATE_EXECUTION_UNIT', 
                    payload: result.data || unit 
                });
                setSuccess('Unità erogante aggiornata con successo');
            } else {
                dispatch({ 
                    type: 'ADD_EXECUTION_UNIT', 
                    payload: result.data || unit 
                });
                setSuccess('Unità erogante creata con successo');
            }
            
            setShowForm(false);
            setEditingUnit(null);
            
            // Ricarica i dati per essere sicuri
            await loadExecutionUnits();
        } catch (err: any) {
            setError(err.message || 'Errore nel salvataggio dell\'unità erogante');
            console.error('Errore nel salvataggio:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingUnit(null);
    };

    // Carica le unità eroganti all'avvio
    useEffect(() => {
        loadExecutionUnits();
    }, []);

    const loadExecutionUnits = async () => {
        setLoading(true);
        setError(null);
        try {
            const units = await getExecutionUnits();
            dispatch({ type: 'SET_EXECUTION_UNITS', payload: units });
        } catch (err: any) {
            setError(err.message || 'Errore nel caricamento delle unità eroganti');
            console.error('Errore nel caricamento delle unità eroganti:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await syncExecutionUnits();
            console.log('Sincronizzazione completata:', result);
            // Ricarica i dati dopo la sincronizzazione
            await loadExecutionUnits();
            setSuccess(`Sincronizzazione completata! Processate: ${result.processed || 0} unità`);
        } catch (err: any) {
            setError(err.message || 'Errore durante la sincronizzazione delle unità eroganti');
            console.error('Errore durante la sincronizzazione:', err);
        } finally {
            setSyncing(false);
        }
    };

    // Filtri le unità eroganti in base ai criteri di ricerca
    const filteredUnits = state.executionUnits.filter(unit => {
        const matchesSearch = searchTerm === '' || 
            unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            unit.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesType = typeFilter === 'all' || 
            unit.type?.toString() === typeFilter;
        
        const matchesStatus = statusFilter === 'all' || 
            unit.status?.toLowerCase() === statusFilter.toLowerCase();
        
        return matchesSearch && matchesType && matchesStatus;
    });

    // Mappa i tipi per il filtro
    const typeOptions = [
        { value: '1', label: 'Ambulatorio' },
        { value: '2', label: 'Laboratorio' },
        { value: '3', label: 'Radiologia' }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5" component="h2">
                            <MedicalServicesIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Gestione Unità Eroganti
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadExecutionUnits}
                                disabled={loading}
                            >
                                Ricarica
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<SyncIcon />}
                                onClick={handleSync}
                                disabled={syncing || loading}
                            >
                                {syncing ? 'Sincronizzazione...' : 'Sincronizza da HealthNET'}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setShowForm(true)}
                            >
                                Aggiungi Unità
                            </Button>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}

                    {/* Filtri */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                placeholder="Cerca per nome, codice o descrizione..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select
                                    value={typeFilter}
                                    label="Tipo"
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    {typeOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Stato"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivo</MenuItem>
                                    <MenuItem value="inactive">Inattivo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Contenuto principale */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Caricamento unità eroganti...</Typography>
                        </Box>
                    ) : showForm ? (
                        <ExecutionUnitForm
                            executionUnit={editingUnit}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    ) : (
                        <>
                            {/* Statistiche */}
                            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<MedicalServicesIcon />}
                                    label={`${filteredUnits.length} unità visualizzate`}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`${filteredUnits.filter(u => u.status === 'active').length} attive`}
                                    color="success"
                                    variant="outlined"
                                />
                                {typeOptions.map(type => {
                                    const count = filteredUnits.filter(u => u.type?.toString() === type.value).length;
                                    return count > 0 ? (
                                        <Chip
                                            key={type.value}
                                            icon={<BusinessIcon />}
                                            label={`${count} ${type.label}`}
                                            color="info"
                                            variant="outlined"
                                        />
                                    ) : null;
                                })}
                            </Box>

                            {/* Tabella unità eroganti */}
                            <TableContainer component={Paper} variant="outlined">
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <MedicalServicesIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Nome Unità
                                                </Box>
                                            </TableCell>
                                            <TableCell>Codice</TableCell>
                                            <TableCell>Tipo</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PlaceIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Ubicazione
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhoneIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                                                    Contatti
                                                </Box>
                                            </TableCell>
                                            <TableCell>Stato</TableCell>
                                            <TableCell align="center">Azioni</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredUnits.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body1" color="textSecondary">
                                                        {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' 
                                                            ? 'Nessuna unità erogante trovata con i filtri applicati'
                                                            : 'Nessuna unità erogante disponibile'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredUnits.map((unit) => (
                                                <TableRow key={unit.id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="subtitle2" fontWeight="bold">
                                                                {unit.name || unit.description}
                                                            </Typography>
                                                            {unit.description && unit.name !== unit.description && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {unit.description}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontFamily="monospace">
                                                            {unit.code}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={typeOptions.find(t => t.value === unit.type?.toString())?.label || 'Non specificato'}
                                                            color="primary"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            {unit.site_name && (
                                                                <Typography variant="body2" fontWeight="medium">
                                                                    {unit.site_name}
                                                                </Typography>
                                                            )}
                                                            {(unit.address || unit.city) && (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {[unit.address, unit.city].filter(Boolean).join(', ')}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            {unit.phone_number && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                    <PhoneIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'action.active' }} />
                                                                    <Typography variant="body2">
                                                                        {unit.phone_number}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                            {unit.email && (
                                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <EmailIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'action.active' }} />
                                                                    <Typography variant="body2">
                                                                        {unit.email}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            icon={unit.status === 'active' ? <CheckCircleIcon /> : <ErrorIcon />}
                                                            label={unit.status === 'active' ? 'Attivo' : 'Inattivo'}
                                                            color={unit.status === 'active' ? 'success' : 'error'}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Tooltip title="Modifica unità">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleEdit(unit)}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Elimina unità">
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleDeleteClick(unit)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dialog di conferma eliminazione */}
            <DeleteConfirmationDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteConfirm}
                title="Elimina Unità Erogante"
                itemName={selectedUnit?.name || selectedUnit?.description || ''}
                entityType="unità erogante"
                allowHardDelete={true}
                userIsAdmin={userIsAdmin}
                relatedData={{
                    code: selectedUnit?.code,
                    type: selectedUnit?.type
                }}
            />
        </Box>
    );
};

export default ExecutionUnitsPage;
